
import { AssetPath } from '@gnsx/genesys.js';
import { BUILT_PROJECT_FOLDER, ENGINE_PATH_PREFIX, PROJECT_PATH_PREFIX } from '@gnsx/genesys.js';
import { AssetPathEncodeState } from '@gnsx/genesys.js';

import { AbstractProvider } from './abstract-provider';


import type { FileChangeEvent, StorageProviderType } from './interfaces';
import type { FileDownloadOptions, FileExistsOptions, IStorageProvider } from './interfaces';
import type { FileListResult, FileUploadOptions } from '@gnsx/genesys.js';

import { GAMES_FOLDER } from '@/app/editor';

/**
 * Scoped Storage provider implementation
 * Implements the IStorageProvider interface by delegating to an underlying provider
 * while constraining all operations within a root path
 */
export class ScopedStorageProvider extends AbstractProvider {
  readonly underlyingProvider: IStorageProvider;
  private publicFolderProvider: IStorageProvider;
  private rootPath: string;


  constructor(underlyingProvider: IStorageProvider, publicFolderProvider: IStorageProvider, rootPath?: string) {
    super();
    this.underlyingProvider = underlyingProvider;
    this.publicFolderProvider = publicFolderProvider;

    if (!rootPath && underlyingProvider.requireProjectContext()) {
      throw new Error('Project context is not set, cannot find project root path');
    }
    this.rootPath = rootPath ?? '';
  }

  requireProjectContext(): boolean {
    // scope is already provided in the constructor, no need to require again
    return false;
  }

  get providerType(): StorageProviderType {
    return this.underlyingProvider.providerType;
  }

  private isEnginePath(path: string): boolean {
    return path.startsWith(ENGINE_PATH_PREFIX);
  }

  private isProjectPath(path: string): boolean {
    return path.startsWith(PROJECT_PATH_PREFIX);
  }

  /**
   * converts a decorated path like a normal path
   * e.g
   * `@project/assets/models/...` -> `games/my-game/assets/models/...`
   * `@engine/assets/models/...` -> `assets/models/...`
   * `assets/models/...` -> `games/my-game/assets/models/...`, if not prefixed with `@project` or `@engine`, it is assumed to be a project path
   */
  private extractFilePath(decoratedPath: string): string {
    // Remove leading/trailing slashes from the path
    let cleanPath = decodeURIComponent(decoratedPath);

    if (this.isProjectPath(cleanPath)) {
      cleanPath = this.rootPath + AssetPath.stripPrefix(cleanPath, PROJECT_PATH_PREFIX);
      // HACK
      // if there are two '.dist/' in cleanPath, remove one,
      // this is a temporary hack to support the paths generated by build game that contains '.dist' already
      cleanPath = cleanPath.replace(`${BUILT_PROJECT_FOLDER}/${BUILT_PROJECT_FOLDER}`, BUILT_PROJECT_FOLDER);

    } else if (this.isEnginePath(cleanPath)) {
      cleanPath = AssetPath.stripPrefix(cleanPath, ENGINE_PATH_PREFIX);
      if (cleanPath.startsWith(GAMES_FOLDER)) {
        throw new Error(`Engine folder cannot contain a ${GAMES_FOLDER} folder, or it collides against project folder`);
      }
    } else {
      // if not prefixed with `@project` or `@engine`, it is assumed to be a project path, for backwards compatibility
      // if (!cleanPath.startsWith(this.rootPath)) {
      //   cleanPath = `${this.rootPath}/${cleanPath}`;
      // }
    }

    return cleanPath;
  }

  private decorateFilePathWith(path: string, prefix: string): string {
    if (prefix === PROJECT_PATH_PREFIX) {
      if (path.startsWith(this.rootPath)) {
        path = path.slice(this.rootPath.length);
      }
    }

    if (path && !path.startsWith('/')) {
      return prefix + '/' + path;
    }
    return prefix + path;
  }

  private getPrefixAndProvider(decoratedPath: string): [string, IStorageProvider] {
    if (this.isEnginePath(decoratedPath)) {
      return [ENGINE_PATH_PREFIX, this.publicFolderProvider];
    } else if (this.isProjectPath(decoratedPath)) {
      return [PROJECT_PATH_PREFIX, this.underlyingProvider];
    }

    // TODO: should return probably `null`, so as to tell the caller to leave the path as is.
    return ['', this.publicFolderProvider];
  }

  async doListFiles(decoratedPath: AssetPath, recursive: boolean): Promise<FileListResult> {
    const filePath = this.extractFilePath(decoratedPath.initialPath);

    const [prefix, provider] = this.getPrefixAndProvider(decoratedPath.initialPath);
    const result: FileListResult = await (provider as AbstractProvider).doListFiles(AssetPath.fromString(filePath), recursive);

    // decorate the paths with the correct prefix
    return {
      files: result.files.map(file => ({
        ...file,
        path: this.decorateFilePathWith(file.path, prefix)
      })),
      directories: result.directories.map(dir => ({
        ...dir,
        path: this.decorateFilePathWith(dir.path, prefix)
      }))
    };
  }

  async uploadFile(
    path: AssetPath,
    content: Blob | File | string | ArrayBuffer,
    options?: FileUploadOptions
  ): Promise<{ path: string; name: string }> {
    const [prefix, provider] = this.getPrefixAndProvider(path.initialPath);
    const filePath = this.extractFilePath(path.initialPath);
    const result = await provider.uploadFile(AssetPath.fromString(filePath), content, options);

    return {
      path: this.decorateFilePathWith(result.path, prefix),
      name: result.name
    };
  }

  async downloadFile(
    path: AssetPath,
    options?: FileDownloadOptions
  ): Promise<Blob | string | ArrayBuffer> {
    const [prefix, provider] = this.getPrefixAndProvider(path.initialPath);
    const filePath = this.extractFilePath(path.initialPath);
    return await provider.downloadFile(AssetPath.fromString(filePath), options);
  }

  async deleteItem(path: AssetPath, recursive?: boolean): Promise<void> {
    const [prefix, provider] = this.getPrefixAndProvider(path.initialPath);
    const filePath = this.extractFilePath(path.initialPath);
    await provider.deleteItem(AssetPath.fromString(filePath), recursive);
  }

  async createDirectory(path: AssetPath): Promise<void> {
    const [prefix, provider] = this.getPrefixAndProvider(path.initialPath);
    const dirPath = this.extractFilePath(path.initialPath);
    await provider.createDirectory(AssetPath.fromString(dirPath));
  }

  async exists(path: AssetPath, options?: FileExistsOptions): Promise<boolean> {
    const [prefix, provider] = this.getPrefixAndProvider(path.initialPath);
    const filePath = this.extractFilePath(path.initialPath);
    return await provider.exists(AssetPath.fromString(filePath), options);
  }

  async resolvePath(path: AssetPath, expiry?: number): Promise<AssetPath> {
    if (path.isResolved()) {
      return path;
    }

    const [prefix, provider] = this.getPrefixAndProvider(path.initialPath);
    if (!prefix) {
      path.resolvePath(path.initialPath, AssetPathEncodeState.Unknown);
      return path;
    }

    const filePath = this.extractFilePath(path.initialPath);
    const resolvedPath = await provider.resolvePath(AssetPath.fromString(filePath), expiry);
    path.resolvePath(resolvedPath.getResolvedPath(), AssetPathEncodeState.Encoded);
    return path;
  }

  public override async buildCurrentProject(): Promise<boolean> {
    return this.underlyingProvider.buildCurrentProject();
  }

  public override onFileSystemChange(callback: (event: FileChangeEvent) => void): () => void {
    return this.underlyingProvider.onFileSystemChange(callback);
  }
}
